import * as yup from "yup";
import moment from "moment";
import { useFormik } from "formik";
import { phoneUtil } from "../../../../../utilities/phone";
import { validateMaskedDob } from "../../../../../shared/InputDob";
import { useCurrentUserQuery } from "../../../../../api/queries/useUserQuery";
import { useCurrentAccountQuery } from "../../../../../api/queries/useAccountQuery";

export function useUpdateForm({ initialValues, onSubmit, isPhoneRequired }) {
  const { data: user } = useCurrentUserQuery();
  const { data: account } = useCurrentAccountQuery();

  const isTouchMd = Boolean(account?.preferences?.isTouchMd);
  const isDobRequired = Boolean(account?.preferences?.requireAppointmentDob);

  const schema = yup.object({
    name: yup.string().required("Name is required"),

    email: yup
      .string()
      .email("Provide valid client email")
      .test({
        message: "Please provide email",
        test: (value) => !(isTouchMd && !value),
      }),

    phone: yup
      .string()
      .test({
        message: "Please provide phone number",
        test: (value) => !(isPhoneRequired && !value),
      })
      .test({
        message: "Provide valid phone number",
        test: (value) => {
          return !value ? true : phoneUtil.isPossible(phoneUtil.compose(value));
        },
      }),

    dob: yup
      .string()
      .test({
        message: "Please provide date of birth",
        test: (value) => !(isDobRequired && !value),
      })
      .test({
        message: "Invalid date",
        test: (value) => {
          return !value ? true : validateMaskedDob(value, user?.dateFormat);
        },
      })
      .test({
        message: "Date of birth cannot be more than 100 years ago",
        test: (value) => {
          if (!value) {
            return true;
          }

          if (validateMaskedDob(value, user?.dateFormat)) {
            return (
              new Date().getFullYear() -
                moment.utc(value, user?.dateFormat).year() <=
              100
            );
          }

          return true;
        },
      }),
  });

  const {
    values,
    errors,
    touched,
    dirty,
    isValid,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    setTouched,
  } = useFormik({
    initialValues,
    validationSchema: schema,
    enableReinitialize: true,
    validateOnChange: true,
    onSubmit,
  });

  const validate = async () => {
    const errors = await setTouched(
      {
        name: true,
        email: true,
        phone: true,
        dob: true,
      },
      true,
    );
    return Object.keys(errors).length === 0;
  };

  const setValue = (field, value) => {
    setFieldTouched(field);
    setFieldValue(field, value);
  };

  const getError = (field) => {
    if (errors[field] && touched[field]) {
      return errors[field];
    }
    return "";
  };

  return {
    form: values,
    setFormValue: setValue,
    getError,
    submit: handleSubmit,
    dirty,
    isValid,
    validate,
  };
}
